import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Typography } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { PARTNER_LOGO_SIZE } from '../partnerDashboardTheme';
import { usePortalTokens } from '../../../styles/usePortalTokens';
import { getPortalCardSx } from '../../../styles/portalSurfaces';
import PartnerLogoAvatar from './PartnerLogoAvatar';

/** Match EventDetailsBanner cover presentation (square crop, centered on dark frame). */
const BANNER_SIZE_WIDTH = { xs: 260, sm: 320 };
const BANNER_SIZE_HEIGHT = { xs: 160, sm: 220 };
const HERO_LOGO_SIZE = PARTNER_LOGO_SIZE / 1.5;
const HERO_LOGO_OVERLAP = HERO_LOGO_SIZE / 2;

const PartnerProfileHero = ({ name = '', description: descriptionProp = '' }) => {
  const navigate = useNavigate();
  const portal = usePortalTokens();
  const { partnerProfile } = useAuth();
  const profileRecord = partnerProfile?.partnerProfile ?? partnerProfile;

  const displayName = partnerProfile?.fullName || profileRecord?.fullName || name;
  const profileDescription =
    partnerProfile?.description ??
    profileRecord?.description ??
    partnerProfile?.aboutDescription ??
    profileRecord?.aboutDescription ??
    descriptionProp;
  const coverBannerUrl = partnerProfile?.coverBannerUrl ?? profileRecord?.coverBannerUrl ?? null;
  const avatarUrl =
    partnerProfile?.avatarUrl ??
    profileRecord?.avatarUrl ??
    partnerProfile?.logoUrl ??
    profileRecord?.logoUrl ??
    undefined;
  const userInitial = (displayName || 'P').charAt(0).toUpperCase();

  return (
    <Card
      elevation={0}
      sx={{
        ...getPortalCardSx(portal, { borderRadius: '12px' }),
        overflow: 'visible',
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: BANNER_SIZE_HEIGHT,
          bgcolor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: BANNER_SIZE_WIDTH,
            height: BANNER_SIZE_HEIGHT,
            maxWidth: '100%',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {coverBannerUrl ? (
            <Box
              component="img"
              src={coverBannerUrl}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: portal.surfaceMuted,
              }}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pb: 2.5,
          pt: `${HERO_LOGO_OVERLAP + 20}px`,
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: 2,
            mt: `-${HERO_LOGO_OVERLAP}px`,
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flex: 1,
              minWidth: 0,
              overflow: 'visible',
            }}
          >
            <PartnerLogoAvatar
              src={avatarUrl}
              alt={displayName}
              initial={userInitial}
              shape="square"
              size={HERO_LOGO_SIZE}
            />
            <Box sx={{ minWidth: 0, flex: 1, overflow: 'visible' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: 'text.primary',
                  lineHeight: 1.3,
                  mb: profileDescription ? 0.5 : 0,
                }}
              >
                {displayName}
              </Typography>
              {profileDescription ? (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8125rem',
                    lineHeight: 1.45,
                    fontWeight: 400,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {profileDescription}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/partner-settings')}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '0.8125rem',
              px: 2,
              py: 0.75,
              flexShrink: 0,
              bgcolor: 'background.paper',
              width: { xs: '100%', sm: 'auto' },
              alignSelf: { xs: 'stretch', sm: 'center' },
            }}
          >
            View Company Profile
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default PartnerProfileHero;
